<template>
  <AppTooltip
    v-if="hoveredRectangle"
    :attach="pdfContainer"
    :key="hoveredRectangle.index"
    :target="hoveredRectangleTooltipPosition"
    :model-value="true"
  >
    {{ $t(
      'common.PdfRedactingSidebar.redactedZoneName',
      { count: hoveredRectangle.index, page: hoveredRectangle.page },
      )
    }}
  </AppTooltip>
</template>

<script>
import AppTooltip from '../AppTooltip.vue'

const MIN_RECTANGLE_SIZE = 5

export default {
  name: 'DocumentPdfViewerRectangleDrawer',
  components: {
    AppTooltip,
  },
  props: {
    pdfAppInstance: {
      type: Object,
      required: true,
    },
    zoomLevel: {
      type: [String, Number],
      required: true,
    },
    isEditing: {
      type: Boolean,
      required: true,
    },
  },
  data () {
    return {
      isDrawing: false,
      startX: 0,
      startY: 0,
      currentX: 0,
      currentY: 0,
      rectangles: [],
      rectangleIndex: 1,

      selectedRectangle: null,
      hoveredRectangle: null,
      offsetX: 0,
      offsetY: 0,
      isMoving: false,
      mouseMovedSignificantly: false,
      isResizing: false,
      resizeCorner: null,
      pdfContainer: this.pdfAppInstance.pdfViewer.container,
      currentPage: 1,
    }
  },
  computed: {
    zoomFactor: {
      get () {
        if (this.zoomLevel === 'auto') {
          return 1
        }
        return this.zoomLevel
      },
    },
    hoveredRectangleTooltipPosition() {
      if (!this.hoveredRectangle) return 'cursor'

      const pageElement = this.pdfContainer.querySelector(`.page[data-page-number="${this.hoveredRectangle.page}"]`)
      if (!pageElement) return 'cursor'

      const canvas = pageElement.querySelector('.annotation-canvas')
      if (!canvas) return 'cursor'

      // Convert rectangle coordinates to canvas pixels
      const rectX = this.hoveredRectangle.x * canvas.width
      const rectY = this.hoveredRectangle.y * canvas.height
      const rectWidth = this.hoveredRectangle.width * canvas.width

      const rectBounds = pageElement.getBoundingClientRect()

      return [
        rectBounds.left + rectX + rectWidth / 2,
        rectBounds.top + rectY,
      ]
    },
  },
  mounted () {
    this.updateCanvasForVisiblePages()
    this.toggleEventListeners()
  },
  beforeUnmount () {
    this.toggleEventListeners(false)
    // Remove all canvas elements currently living
    const visiblePages = this.getVisiblePages()
    visiblePages.forEach((pageNumber) => {
      const pageElement = this.pdfContainer.querySelector(`.page[data-page-number="${pageNumber}"]`)
      if (pageElement && pageElement.querySelector('.annotation-canvas')) {
        pageElement.removeChild(pageElement.querySelector('.annotation-canvas'))
      }
    })
    this.rectangles = []
    this.updateRectangles()
  },
  watch: {
    isEditing () {
      this.updateCanvasForVisiblePages()
    },
    hoveredRectangle () {
      this.drawAllRectangles()
    },
  },
  methods: {
    toggleEventListeners (enabled = true) {
      let eventBusMethod = 'on'
      let domEventListenerMethod = 'addEventListener'
      if (!enabled) {
        eventBusMethod = 'off'
        domEventListenerMethod = 'removeEventListener'
      }
      this.pdfAppInstance.eventBus[eventBusMethod]('pagechanging', this.updateCanvasForVisiblePages)
      this.pdfAppInstance.eventBus[eventBusMethod]('scalechanging', this.onScaleChange)
      this.pdfAppInstance.eventBus[eventBusMethod]('pagerendered', this.onPageRendered)

      this.pdfContainer[domEventListenerMethod]('scroll', this.updateCanvasForVisiblePages)
      window[domEventListenerMethod]('resize', this.updateCanvasForVisiblePages)
    },
    onScaleChange () {
      const pageElements = this.pdfContainer.querySelectorAll('.page')
      pageElements.forEach((pageElement) => {
        if (!pageElement.classList.contains('pending-rerender')) {
          // Add a class to indicate that it isn't rendered yet
          pageElement.classList.add('pending-rerender')
        }
      })
    },
    onPageRendered (e) {
      const pageElement = this.pdfContainer.querySelector(`.page[data-page-number="${e.pageNumber}"]`)
      if (pageElement && pageElement.classList.contains('pending-rerender')) {
        pageElement.classList.remove('pending-rerender')
      }
      this.updateCanvasForVisiblePages()
    },
    updateCanvasForVisiblePages () {
      if (!this.isEditing) return

      const visiblePages = this.getVisiblePages()
      visiblePages.forEach((pageNumber) => {
        const pageElement = this.pdfContainer.querySelector(`.page[data-page-number="${pageNumber}"]`)
        if (pageElement && pageElement.querySelector('.annotation-canvas')) {
          pageElement.removeChild(pageElement.querySelector('.annotation-canvas'))
        }

        // Create a canvas for each visible page
        const canvas = document.createElement('canvas')
        canvas.className = 'annotation-canvas'
        canvas.style.position = 'absolute'
        canvas.style.top = '0'
        canvas.style.left = '0'
        canvas.width = pageElement.clientWidth
        canvas.height = pageElement.clientHeight
        canvas.style.zIndex = 999 // For PDFs containing links that are above the canvas

        // Add event listeners to the canvas
        canvas.addEventListener('mousemove', (event) => {
          let isOverHandle = false
          // Change cursor when hovering over handles
          const resizeCursors = {
            topLeft: 'nwse-resize',
            bottomRight: 'nwse-resize',
            topRight: 'nesw-resize',
            bottomLeft: 'nesw-resize',
          }
          this.hoveredRectangle = this.rectangles.find((rect) => {
            if (rect.page !== pageNumber) {
              return false
            }

            const handles = this.getHandlesUnderCursor(event, rect, canvas)
            for (const handle in handles) {
              if (handles[handle]) {
                canvas.style.cursor = resizeCursors[handle] || 'default'
                isOverHandle = true
                return true
              }
            }
            return false
          })

          if (!isOverHandle) {
            this.hoveredRectangle = this.getRectangleUnderCursor(event, pageNumber, canvas)
            if (!this.isResizing) {
              if (this.hoveredRectangle) {
                canvas.style.cursor = 'move'
              } else if (this.isEditing) {
                canvas.style.cursor = `url(${new URL('../../assets/img/redact-cursor.svg', import.meta.url).href}) 8 8, crosshair`
                this.hoveredRectangle = null
              } else {
                canvas.style.cursor = 'default'
                this.hoveredRectangle = null
              }
            }
          }

          if (this.isResizing) {
            this.resizeRectangle(event, canvas)
          } else if (this.isMoving) {
            this.moveRectangle(event, canvas)
          } else if (this.isDrawing) {
            this.drawRectangle(event, canvas)
          }
        })

        canvas.addEventListener('mousedown', (event) => {
          if (event.button !== 0) return
          // Check if handle was clicked
          const clickedRectangle = this.rectangles.find((rect) => {
            if (rect.page !== pageNumber) {
              return false
            }

            const handles = this.getHandlesUnderCursor(event, rect, canvas)
            for (const handle in handles) {
              if (handles[handle]) {
                this.startResizing(rect, handle)
                return true
              }
            }
            return false
          })

          // If no handle was clicked, check if rectangle was clicked
          if (!clickedRectangle) {
            const rectangle = this.getRectangleUnderCursor(event, pageNumber, canvas)
            if (rectangle) {
              this.startMoving(event, rectangle, canvas)
              canvas.style.cursor = 'move'
            } else if (this.isEditing) {
              this.startDrawing(event, pageNumber, canvas)
              canvas.style.cursor = 'crosshair'
            }
          }
        })

        canvas.addEventListener('mouseup', (event) => {
          if (this.isResizing) {
            this.finishResizing()
          } else if (this.isMoving) {
            this.finishMoving(event)
          } else if (this.isDrawing) {
            this.finishDrawing(canvas)
          }
          this.hoveredRectangle = null
        })

        canvas.addEventListener('mouseleave', (event) => {
          const pages = this.pdfContainer.querySelectorAll('.page')
          if (pages.length > 0) {
            // Check if mouse is leaving the canvas horizontally or first page above or last page below
            // Because we can move rectangles to another page vertically
            const rect = canvas.getBoundingClientRect()
            const mouseY = event.clientY
            const isVerticalExit = mouseY < rect.top || mouseY > rect.bottom

            const firstPage = parseInt(pages[0].getAttribute('data-page-number'), 10)
            const isAboveFirstPage = (pageNumber == firstPage) && mouseY < rect.top

            const lastPage = parseInt(pages[pages.length - 1].getAttribute('data-page-number'), 10)
            const isBelowLastPage = (pageNumber == lastPage) && mouseY > rect.bottom

            // If mouse is leaving the canvas, finish the action
            if (!isVerticalExit || isAboveFirstPage || isBelowLastPage) {
              if (this.isResizing) {
                this.finishResizing()
              } else if (this.isMoving) {
                this.finishMoving(event)
              } else if (this.isDrawing) {
                this.finishDrawing(canvas)
              }
            }
          }
          this.hoveredRectangle = null
        })

        pageElement.appendChild(canvas)
      })

      this.drawAllRectangles()
    },
    /**
     * DRAWING METHODS
     */
    startDrawing(event, pageNumber, canvas) {
      this.pdfContainer.classList.add('no-text-selection')

      const rect = canvas.getBoundingClientRect()
      this.startX = event.clientX - rect.left
      this.startY = event.clientY - rect.top
      this.isDrawing = true
      this.mouseMovedSignificantly = false
      this.currentPage = pageNumber
    },
    drawRectangle(event, canvas) {
      if (!this.isDrawing) return

      const rect = canvas.getBoundingClientRect()
      this.currentX = event.clientX - rect.left
      this.currentY = event.clientY - rect.top

      // Check if mouse has significantly moved at least 10 pixels
      if (Math.abs(this.currentX - this.startX) > 10 || Math.abs(this.currentY - this.startY) > 10) {
        this.mouseMovedSignificantly = true
      } else { // If not, don't draw anything
        return
      }

      this.drawAllRectangles()

      // Draw the current rectangle
      const ctx = canvas.getContext('2d')
      ctx.fillStyle = '#9ddd6e80'
      ctx.strokeStyle = '#9ddd6e80'
      const width = this.currentX - this.startX
      const height = this.currentY - this.startY
      ctx.fillRect(this.startX, this.startY, width, height)
      ctx.strokeRect(this.startX, this.startY, width, height)
    },
    finishDrawing(canvas) {
      if (!this.isDrawing) return

      // Do not create rectangle if mouse didn't move significantly
      if (!this.mouseMovedSignificantly) {
        this.isDrawing = false
        return
      }

      // Calculate rectangle coordinates
      let x1 = this.startX / canvas.width
      let y1 = this.startY / canvas.height
      let x2 = this.currentX / canvas.width
      let y2 = this.currentY / canvas.height

      // Normalize coordinates regardless of drawing direction
      const x = Math.min(x1, x2)
      const y = Math.min(y1, y2)
      const width = Math.abs(x2 - x1)
      const height = Math.abs(y2 - y1)

      this.rectangles.push({
        page: this.currentPage,
        x,
        y,
        width,
        height,
        index: this.rectangleIndex++,
      })

      this.isDrawing = false
      this.drawAllRectangles()
      this.updateRectangles()
    },
    /**
     * MOVING METHODS
     */
    startMoving(event, rect, canvas) {
      this.isMoving = true
      this.selectedRectangle = rect

      this.pdfContainer.classList.add('no-text-selection')

      const rectBounds = canvas.getBoundingClientRect()

      // Convert normalized coordinates into real canvas pixels
      const rectX = rect.x * canvas.width
      const rectY = rect.y * canvas.height

      this.offsetX = event.clientX - rectBounds.left - rectX
      this.offsetY = event.clientY - rectBounds.top - rectY
    },
    moveRectangle(event, canvas) {
      if (!this.isMoving || !this.selectedRectangle) return

      const rectBounds = canvas.getBoundingClientRect()

      // Convert mouse position to canvas pixels
      const newX = event.clientX - rectBounds.left - this.offsetX
      const newY = event.clientY - rectBounds.top - this.offsetY

      // Normalize coordinates as percentage of the canvas
      this.selectedRectangle.x = newX / canvas.width
      this.selectedRectangle.y = newY / canvas.height

      // Check if rectangle is being moved to another page
      const pageElements = this.pdfContainer.querySelectorAll('.page')
      pageElements.forEach((pageElement) => {
        const pageRect = pageElement.getBoundingClientRect()
        if (
          event.clientX >= pageRect.left &&
          event.clientX <= pageRect.right &&
          event.clientY >= pageRect.top &&
          event.clientY <= pageRect.bottom
        ) {
          // Move to the new page if necessary
          const newPage = parseInt(pageElement.getAttribute('data-page-number'), 10)
          if (newPage !== this.selectedRectangle.page) {
            this.selectedRectangle.page = newPage
          }
        }
      })

      this.drawAllRectangles()
    },
    finishMoving(event) {
      if (!this.isMoving || !this.selectedRectangle) return

      const pageElements = this.pdfContainer.querySelectorAll('.page')
      pageElements.forEach((pageElement) => {
        const pageRect = pageElement.getBoundingClientRect()
        if (
          event.clientX >= pageRect.left &&
          event.clientX <= pageRect.right &&
          event.clientY >= pageRect.top &&
          event.clientY <= pageRect.bottom
        ) {
          const newPage = parseInt(pageElement.getAttribute('data-page-number'), 10)
          if (newPage !== this.selectedRectangle.page) {
            this.selectedRectangle.page = newPage

            // Get the canvas for the new page
            const canvas = pageElement.querySelector('.annotation-canvas')
            if (canvas) {
              // Calculate x and y as percentage of the canvas to avoid zoom problems
              this.selectedRectangle.x = (event.clientX - pageRect.left) / canvas.width
              this.selectedRectangle.y = (event.clientY - pageRect.top) / canvas.height
            }
          }
        }
      })

      // Prevent overflow of the rectangle outside the page
      this.clampRectanglePosition()

      this.isMoving = false
      this.selectedRectangle = null

      this.drawAllRectangles()
    },
    clampRectanglePosition() {
      const pageElement = this.pdfContainer.querySelector(`.page[data-page-number="${this.selectedRectangle.page}"]`)
      if (!pageElement) return

      const canvas = pageElement.querySelector('.annotation-canvas')
      if (!canvas) return

      // Set canvas percentage limits
      const maxX = Math.max(0, 1 - this.selectedRectangle.width)
      const maxY = Math.max(0, 1 - this.selectedRectangle.height)

      this.selectedRectangle.x = Math.max(0, Math.min(this.selectedRectangle.x, maxX))
      this.selectedRectangle.y = Math.max(0, Math.min(this.selectedRectangle.y, maxY))
    },
    /**
    * RESIZING METHODS
    */
    startResizing(rect, handle) {
      this.isResizing = true
      this.selectedRectangle = rect
      this.resizeCorner = null
      this.resizeCorner = handle
    },
    resizeRectangle(event, canvas) {
      if (!this.isResizing || !this.selectedRectangle) return

      // Convert offsetX and offsetY to canvas pixels
      const rectBounds = canvas.getBoundingClientRect()
      const offsetX = (event.clientX - rectBounds.left) / canvas.width
      const offsetY = (event.clientY - rectBounds.top) / canvas.height

      this.resizeByCorner(offsetX, offsetY, canvas)

      this.drawAllRectangles()
    },
    resizeByCorner(offsetX, offsetY, canvas) {
      let newX = this.selectedRectangle.x
      let newY = this.selectedRectangle.y
      let newWidth = this.selectedRectangle.width
      let newHeight = this.selectedRectangle.height

      if (this.resizeCorner === 'topLeft') {
        newWidth = this.selectedRectangle.x + this.selectedRectangle.width - offsetX
        newHeight = this.selectedRectangle.y + this.selectedRectangle.height - offsetY
        newX = offsetX
        newY = offsetY
      } else if (this.resizeCorner === 'topRight') {
        newWidth = offsetX - this.selectedRectangle.x
        newHeight = this.selectedRectangle.y + this.selectedRectangle.height - offsetY
        newY = offsetY
      } else if (this.resizeCorner === 'bottomLeft') {
        newWidth = this.selectedRectangle.x + this.selectedRectangle.width - offsetX
        newHeight = offsetY - this.selectedRectangle.y
        newX = offsetX
      } else if (this.resizeCorner === 'bottomRight') {
        newWidth = offsetX - this.selectedRectangle.x
        newHeight = offsetY - this.selectedRectangle.y
      }

      // Check that minimum size is respected
      if (newWidth > MIN_RECTANGLE_SIZE / canvas.width && newHeight > MIN_RECTANGLE_SIZE / canvas.height) {
        this.selectedRectangle.x = newX
        this.selectedRectangle.y = newY
        this.selectedRectangle.width = newWidth
        this.selectedRectangle.height = newHeight
      }
    },
    finishResizing() {
      this.isResizing = false
      this.selectedRectangle = null
      this.resizeCorner = null
      this.resizeSide = null
    },
    drawAllRectangles() {
      const pageElement = this.pdfContainer.querySelector(`.page[data-page-number="${this.currentPage}"]`)
      if (pageElement && pageElement.classList.contains('pending-rerender')) {
        // Do not draw if the page loading
        return
      }
      const visiblePages = this.getVisiblePages()
      visiblePages.forEach((pageNumber) => {
        const pageElement = this.pdfContainer.querySelector(`.page[data-page-number="${pageNumber}"]`)
        const canvas = pageElement.querySelector('.annotation-canvas')
        if (!canvas) return

        const ctx = canvas.getContext('2d')
        ctx.clearRect(0, 0, canvas.width, canvas.height)

        // Draw rectangles
        const pageRectangles = this.rectangles.filter(rect => rect.page === pageNumber)
        pageRectangles.forEach((rect) => {
          ctx.beginPath()
          ctx.fillStyle = '#9ddd6e80'

          const isHoveredOrSelected = this.areRectanglesEqual(this.hoveredRectangle, rect) ||
                                      this.areRectanglesEqual(this.selectedRectangle, rect)
          if (isHoveredOrSelected) {
            ctx.strokeStyle = '#1a73e8'
            ctx.lineWidth = 1.5
          } else {
            ctx.strokeStyle = '#9ddd6e80'
          }

          // Draw rectangle with zoom factor
          const adjustedX = rect.x * canvas.width
          const adjustedY = rect.y * canvas.height
          const adjustedWidth = rect.width * canvas.width
          const adjustedHeight = rect.height * canvas.height

          ctx.fillRect(
            adjustedX,
            adjustedY,
            adjustedWidth,
            adjustedHeight,
          )
          ctx.strokeRect(
            adjustedX,
            adjustedY,
            adjustedWidth,
            adjustedHeight,
          )

          if (isHoveredOrSelected) {
            this.drawHandlesForResizing(ctx, rect, canvas)
          }
        })
      })
    },
    areRectanglesEqual(rect1, rect2) {
      if (!rect1 || !rect2) return false
      if (rect1.page !== rect2.page) return false

      const tolerance = 0.001 // Allows slight margin of error on float values

      return (
        Math.abs(rect1.x - rect2.x) < tolerance &&
        Math.abs(rect1.y - rect2.y) < tolerance &&
        Math.abs(rect1.width - rect2.width) < tolerance &&
        Math.abs(rect1.height - rect2.height) < tolerance
      )
    },
    drawHandlesForResizing(ctx, rect, canvas) {
      const handleSize = Math.max(5, 5 * this.zoomFactor)
      ctx.fillStyle = '#ffffff'

      // Retrieve rectangle's dimensions from the canvas
      const rectX = rect.x * canvas.width
      const rectY = rect.y * canvas.height
      const rectWidth = rect.width * canvas.width
      const rectHeight = rect.height * canvas.height

      const handles = [
        { x: rectX, y: rectY }, // Top-left
        { x: rectX + rectWidth, y: rectY }, // Top-right
        { x: rectX, y: rectY + rectHeight }, // Bottom-left
        { x: rectX + rectWidth, y: rectY + rectHeight }, // Bottom-right
      ]

      handles.forEach(handle => {
        ctx.beginPath()
        ctx.arc(handle.x, handle.y, handleSize, 0, Math.PI * 2) // Circle
        ctx.fill()
        ctx.stroke()
      })
    },
    getRectangleUnderCursor(event, pageNumber, canvas) {
      return this.rectangles.find((rect) => {
        // Convert rectangle coordinates to canvas pixels
        const rectX = rect.x * canvas.width
        const rectY = rect.y * canvas.height
        const rectWidth = rect.width * canvas.width
        const rectHeight = rect.height * canvas.height

        return (
          rect.page === pageNumber &&
          event.offsetX >= rectX &&
          event.offsetX <= rectX + rectWidth &&
          event.offsetY >= rectY &&
          event.offsetY <= rectY + rectHeight
        )
      })
    },
    getHandlesUnderCursor(event, rect, canvas) {
      const handleSize = Math.max(10, 20 * this.zoomFactor)
      const mouseX = event.offsetX
      const mouseY = event.offsetY

      // Convert normalized coordinates into canvas pixels
      const rectX = rect.x * canvas.width
      const rectY = rect.y * canvas.height
      const rectWidth = rect.width * canvas.width
      const rectHeight = rect.height * canvas.height

      const isInside = (handleX, handleY) => (
        mouseX >= handleX - handleSize / 2 &&
        mouseX <= handleX + handleSize / 2 &&
        mouseY >= handleY - handleSize / 2 &&
        mouseY <= handleY + handleSize / 2
      )

      return {
        topLeft: isInside(rectX, rectY),
        topRight: isInside(rectX + rectWidth, rectY),
        bottomLeft: isInside(rectX, rectY + rectHeight),
        bottomRight: isInside(rectX + rectWidth, rectY + rectHeight),
      }
    },
    getVisiblePages () {
      const visiblePages = []
      const pageElements = this.pdfContainer.querySelectorAll('.page')
      pageElements.forEach((pageElement) => {
        const boundingRect = pageElement.getBoundingClientRect()
        // Check if page is visible in viewport
        if (
          boundingRect.top < this.pdfContainer.clientHeight &&
          boundingRect.bottom > 0
        ) {
          const pageNumber = parseInt(pageElement.getAttribute('data-page-number'), 10)
          visiblePages.push(pageNumber)
        }
      })
      return visiblePages
    },
    updateRectangles () {
      this.$emit('updateRectangles', this.rectangles)
    },
  },
}
</script>

<style>
.annotation-canvas {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 2;
  pointer-events: all;
}

.no-text-selection {
  user-select: none; /* Disable text selection */
  -webkit-user-select: none; /* For Safari */
}
</style>
